import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import {
  ContentSwitcher,
  DataLabelsAndColors,
  URLS,
  useCallIntelDefaultsShallowStore,
  useCallIntelNavigator,
} from '@frontend/call-intel-core';
import { i18next, useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { CallIntelDemoBanner } from '../components/call-intelligence/call-intel-demo-banner';
import { CallRecordingsBanner } from '../components/call-intelligence/call-recordings-banner';
import { LocationComparisonCharts } from '../components/call-intelligence/comparison-view/location-comparison-charts';
import { CallIntelOverviewFilters } from '../components/call-intelligence/filters/organisms/call-intel-overview-filters';
import type { FilterValues } from '../components/call-intelligence/filters/types';
import { FiltersSection } from '../components/call-intelligence/filters-section';
import { useActiveFilters } from '../components/call-intelligence/hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../components/call-intelligence/hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../components/call-intelligence/hooks/use-call-intel-locations';
import {
  useCallIntelShallowStore,
  type CallIntelSubViewId,
  type CallIntelSubViewType,
  type MultiLocationsView,
} from '../components/call-intelligence/hooks/use-call-intel-store';
import { useLocationsMatch } from '../components/call-intelligence/hooks/use-locations-match';
import { useOfficeUsers } from '../components/call-intelligence/hooks/use-office-users';
import { SubViewHeader } from '../components/call-intelligence/sub-view-header';
import { OpportunitiesBySentimentsSubView } from '../components/call-intelligence/sub-views/opportunities-by-sentiments-sub-view';
import { OpportunitiesByTypeSubView } from '../components/call-intelligence/sub-views/opportunities-by-type-sub-view';
import { CallRecordsTable } from '../components/call-intelligence/tables/call-records-table';
import { ScrollClassTrigger } from '../components/scroll-class-trigger';
import { trackingIds } from '../tracking-ids';
import { callIntelligenceUtils } from '../utils/call-intelligence';
import { callIntelResponsiveStyles } from './common-styles';

type PageTypeConfig = {
  filterKey?: keyof CallIntelTypes.Filters;
  getLabel: (dataLabels: DataLabelsAndColors, pageId: string) => string;
  getMetrics: (dataLabels: DataLabelsAndColors, pageId: string) => string;
  title: string;
  withHeading: boolean;
};

const pageTypeConfig: Partial<Record<CallIntelSubViewType, PageTypeConfig>> = {
  'appointment-type': {
    filterKey: 'appointmentTypes',
    getLabel: (dataLabels, pageId) => dataLabels.appointmentTypes?.[pageId] || '',
    getMetrics: (dataLabels, pageId) => dataLabels.appointmentTypes?.[pageId] || '',
    title: i18next.t('Appointment Type', { ns: 'analytics' }),
    withHeading: true,
  },
  category: {
    filterKey: 'categories',
    getLabel: (dataLabels, pageId) => dataLabels.categories?.[pageId] || '',
    getMetrics: (dataLabels, pageId) => dataLabels.categories?.[pageId] || '',
    title: i18next.t('Category', { ns: 'analytics' }),
    withHeading: true,
  },
  sentiment: {
    filterKey: 'sentiments',
    getLabel: (dataLabels, pageId) => dataLabels.sentimentsWithEmoji?.[pageId] || '',
    getMetrics: (dataLabels, pageId) => dataLabels.sentiments?.[pageId] || '',
    title: i18next.t('Customer Sentiment', { ns: 'analytics' }),
    withHeading: true,
  },
};

const getPageConfig = (pageType: CallIntelSubViewType): PageTypeConfig | null => {
  return pageTypeConfig[pageType] || null;
};

export const CallIntelSubView = () => {
  const { t } = useTranslation('analytics');

  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { hasValidSubView } = useCallIntelNavigator();

  const { isDemoModal, isDemoAccount } = useCallIntelDemoFlags();
  const { filterHintText, subView } = useCallIntelShallowStore('filterHintText', 'subView');

  const { filters, updateFilterValues } = useActiveFilters();

  const pageRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const { isMultiLocation, locations } = useCallIntelLocations();

  const areLocationsMatching = useLocationsMatch(locations);
  const [filtersReady, setFiltersReady] = useState<boolean>(false);
  const [activeMultiView, setActiveMultiView] = useState<MultiLocationsView>('summary');
  const [isLoadingChartsData, setIsLoadingChartsData] = useState<boolean>(false);
  const pageId = subView.id as CallIntelSubViewId;
  const pageType = subView.type;
  const pageConfig = pageType ? getPageConfig(pageType) : null;
  const pageLabel = pageConfig ? pageConfig.getLabel(dataLabels, pageId) : '';
  const metricsFor = pageConfig ? pageConfig.getMetrics(dataLabels, pageId) : '';
  const pageTypeTitle = pageConfig?.title || '';

  const drillDownFilters = useMemo(() => callIntelligenceUtils.getDrillDownFilterKeys(subView), [subView]);

  const { activeOfficeUsersForFilter } = useOfficeUsers({
    isDemoLocation: filters.locations?.[0]?.includes('demo'),
    locationIds: filters.locations || [],
  });

  const getSubView = () => {
    switch (pageType) {
      case 'appointment-type':
      case 'category':
        return {
          overview: (
            <OpportunitiesByTypeSubView metricsFor={metricsFor} onChangeLoadingState={setIsLoadingChartsData} />
          ),
        };

      case 'sentiment':
        return {
          overview: (
            <OpportunitiesBySentimentsSubView
              metricsFor={metricsFor}
              onChangeLoadingState={setIsLoadingChartsData}
              sentimentCode={pageId}
            />
          ),
        };

      default:
        return null;
    }
  };

  const getPageSubtitleText = () =>
    `${
      isMultiLocation && filters.locations?.length
        ? filters.locations.length === Object.keys(locations).length
          ? t('all locations, ')
          : filters.locations.length === 1
          ? t('1 location, ')
          : t('{{count}} locations, ', { count: filters.locations.length })
        : ''
    }${filterHintText}`;

  useEffect(() => {
    // Navigate to the overveiw page when main location changed while viewing drill down page
    if (!areLocationsMatching) {
      navigate({ to: URLS.CALL_INTEL_MAIN_PAGE });
    }
  }, [areLocationsMatching, navigate]);

  useEffect(() => {
    // If search params exist, allow useInitCallIntelFilters to hydrate the store first
    if (!hasValidSubView(subView)) {
      navigate({ to: URLS.CALL_INTEL_MAIN_PAGE });
      return;
    }
    const filtersToUpdate: FilterValues = {};

    // In case of multi location comparison chart click, the search param will have the clicked location id
    // and the filters state will be updated accordingly
    const searchLocationId = search?.locationId as string;
    if (searchLocationId) {
      filtersToUpdate.locations = [searchLocationId];
    }

    const { key, value } = drillDownFilters || {};
    switch (key) {
      case 'appointment_types':
        filtersToUpdate.appointmentTypes = value as CallIntelTypes.AppointmentTypeEnum[];
        break;

      case 'categories':
        filtersToUpdate.categories = value as CallIntelTypes.CategoryEnum[];
        break;

      case 'sentiments':
        filtersToUpdate.sentiments = value as CallIntelTypes.SentimentEnum[];
        break;

      default:
        break;
    }

    if (Object.keys(filtersToUpdate).length > 0) {
      updateFilterValues(filtersToUpdate);
    }

    // Using it to avoid multiple api calls on initial load
    setFiltersReady(true);
    // Only depend on primitive values, not object references to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasValidSubView,
    subView?.id,
    subView?.type,
    search?.locationId,
    navigate,
    drillDownFilters?.key,
    updateFilterValues,
  ]);

  const { hideChipFilters, hideFilters } = useMemo(() => {
    const _hideChipFilters: (keyof CallIntelTypes.Filters)[] = drillDownFilters
      ? ['locations', 'startDate', 'endDate', 'contactTypes']
      : [];

    const _hideFilters: (keyof CallIntelTypes.Filters)[] = [];

    if (pageConfig?.filterKey) {
      _hideFilters.push(pageConfig.filterKey);
    }
    // Hide Service issue filter when on positive/neutral sentiment pages
    if (
      pageType === 'sentiment' &&
      [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE, CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL].includes(
        pageId as CallIntelTypes.SentimentEnum
      )
    ) {
      _hideFilters.push('serviceQualityFlags');
    }

    return { hideChipFilters: _hideChipFilters, hideFilters: _hideFilters };
  }, [drillDownFilters, pageConfig?.filterKey, pageId, pageType]);

  const hideColumns = useMemo<string[]>(() => {
    const columnsToHide: string[] = [];

    // Hide Service issue column when on positive/neutral sentiment pages
    if (
      pageType === 'sentiment' &&
      [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE, CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL].includes(
        pageId as CallIntelTypes.SentimentEnum
      )
    ) {
      columnsToHide.push('serviceQualityFlags');
    }

    return columnsToHide;
  }, [pageType, pageId]);

  return (
    <>
      <CallIntelDemoBanner />
      <CallRecordingsBanner />
      {/* This is used to apply shadow class on header in small mode on scroll */}
      <ScrollClassTrigger targetRef={headerRef} outOfViewClassName='sticky-shadow' />
      <Page css={callIntelResponsiveStyles.page} ref={pageRef} maxWidth='100%'>
        <SubViewHeader
          headerRef={headerRef}
          title={pageLabel}
          subtitle={t('Showing results for {{time}}', { time: getPageSubtitleText() })}
          breadcrumbs={[
            {
              label: isDemoModal ? '' : t('Analytics'),
              to: '/analytics/dashboard',
            },
            {
              label: t('Call Intelligence'),
              to: URLS.CALL_INTEL_MAIN_PAGE,
            },
            {
              label: `${metricsFor} ${pageTypeTitle}`,
            },
          ]}
          feedbackTrackingId={trackingIds.callIntel.generalFeedbackDrillDownPage}
        />
        <Page.Body>
          <FiltersSection data-testid='call-intel-sub-view-filters-section'>
            <CallIntelOverviewFilters isLoading={isLoadingChartsData}>
              <CallIntelOverviewFilters.Filters officeUsers={activeOfficeUsersForFilter} />
              <CallIntelOverviewFilters.Actions>
                {isMultiLocation && (
                  <div css={callIntelResponsiveStyles.multiComparison} data-testid='multi-comparison-content-switcher'>
                    <ContentSwitcher<MultiLocationsView>
                      defaultValue={activeMultiView}
                      onValueChange={setActiveMultiView}
                    >
                      <ContentSwitcher.Option
                        label={t('Summary View')}
                        iconName='building-small'
                        value='summary'
                        trackingId='summary-view'
                      />
                      <ContentSwitcher.Option
                        label={t('Locations View')}
                        iconName='locations-small'
                        value='betweenLocations'
                        trackingId='locations-view'
                      />
                    </ContentSwitcher>
                  </div>
                )}
              </CallIntelOverviewFilters.Actions>
            </CallIntelOverviewFilters>
          </FiltersSection>
          {filtersReady && (
            <div css={[callIntelResponsiveStyles.contentWrapper, styles.contentWrapper]}>
              <div css={styles.summaryCardsContainer}>
                {activeMultiView === 'summary' ? (
                  getSubView()?.overview
                ) : (
                  <LocationComparisonCharts
                    drillDownFilters={drillDownFilters}
                    internalPage
                    onChangeLoadingState={setIsLoadingChartsData}
                  />
                )}
              </div>

              <div data-testid='call-records-section'>
                {pageConfig?.withHeading && (
                  <Heading css={styles.tableHeader} level={3}>
                    {isDemoAccount && <DemoChip />}
                    {t('{{name}} Call Recordings', { name: metricsFor })}
                  </Heading>
                )}

                <CallRecordsTable
                  drillDownFilters={drillDownFilters}
                  hideFilters={hideFilters}
                  hideChipFilters={hideChipFilters}
                  hideColumns={hideColumns}
                  isDemoAccount={isDemoAccount}
                  marginTop={16}
                  mobileViewHeight={'min(450px, 60vh)'}
                />
              </div>
            </div>
          )}
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  tableHeader: css`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(0.5)};

    > span {
      font-size: inherit;
    }
  `,
  contentWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(5)};

    @container (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(2)};
    }
  `,
  summaryCardsContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};

    @container (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(2)};
    }
  `,
};
